import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { apiRequest } from "utils/api";

export function useRoles({
  userType,
  company_id,
}: {
  userType?: "lender" | "borrower";
  company_id?: string;
}) {
  const queryClient = useQueryClient();
  const { user } = useTenant();

  const _userType = userType;
  const _company_id = company_id;

  const { data: roles, isLoading: isRolesLoading } = useQuery({
    queryKey: ["/supabase/roles/select", { company_id: _company_id, type: _userType }],
    queryFn: () =>
      apiRequest("/supabase/roles/select", {
        type: _userType,
        company_id: _company_id,
      }),
    select: data => data?.roles ?? [],
    enabled: !!_userType && !!_company_id,
  });

  const { data: members, isLoading: isMembersLoading } = useQuery({
    queryKey: ["/supabase/users/select", { type: _userType, company_id: _company_id }],
    queryFn: () =>
      apiRequest(`/supabase/users/select`, {
        type: _userType,
        company_id: _company_id,
      }),
    select: data => data?.users ?? [],
    enabled: !!_userType && !!_company_id,
  });

  const { data: permissions, isLoading: isPermissionsLoading } = useQuery({
    queryKey: ["/supabase/permissions/select"],
    queryFn: () => apiRequest("/supabase/permissions/select"),
    select: data =>
      (data?.allPermissions ?? [])?.filter((x: any) => {
        return _userType === "lender" ? true : x.permission_name !== "edit_branding";
      }),
    enabled: true,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["/supabase/roles/select"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["/supabase/users/select"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["/supabase/permissions/select"],
    });
  };

  return {
    roles,
    permissions,
    members,
    invalidate,
    isLoading: isRolesLoading || isMembersLoading || isPermissionsLoading,
  };
}
